import { useMemo } from "react";
import { useAuth } from "../store/hooks/useAuth";
import { useFeatureFlags } from "../contexts/FeatureFlagsContext";
import {
  useEffectivePermissions,
  useHasPermission,
} from "../store/hooks/useRbac";
import { FEATURE_FLAGS, RoleTypeEnum } from "../utils/constants";
import { PERMISSIONS } from "../utils/rbac/permissions";
import {
  getPathForPermissionCode,
  getFeatureFlagForPermissionCode,
} from "../utils/rbac/sidebarMapping";
import {
  resolveSidebarModuleIcon,
  resolveSidebarSubmoduleIcon,
} from "../utils/sidebarIcons";
import {
  LayoutDashboard,
  Calendar,
  Clock,
  Users,
  FileText,
  Settings,
  CheckSquare,
  PlusCircle,
  Star,
  CreditCard,
  RefreshCcw,
  Eye,
  Sliders,
  User,
  CircleHelp,
  ToggleLeft,
  Receipt,
  Package,
  Box,
  Target,
  UserCog,
  Network,
  BarChart,
  Split,
  AlignCenterVerticalIcon,
  Award,
  Crown,
  Medal,
  FileStack,
  NotepadText,
  ClipboardPen,
  HandHelping,
  UserRoundCog,
  FileUser,
  SquareUserRound,
  CalendarClock,
  UsersRound,
  UserPlus,
  Users2Icon,
  DoorOpen,
  Mail,
  Building2,
} from "lucide-react";

export interface SidebarMenuItem {
  icon: unknown;
  label: string;
  path?: string; // Optional for group items
  type?: "single" | "group";
  items?: SidebarMenuItem[];
  code?: string; // Permission code for dynamic updates
  disabled?: boolean; // For unmapped modules/submodules visible to admins
  badge?: number | string;
}

/**
 * Hook to get sidebar menu structure with dynamic names from backend
 * Uses hierarchy from rbac slice (useEffectivePermissions) to avoid duplicate API calls
 */
export const useSidebarMenu = () => {
  const { user } = useAuth();
  const { isEnabled } = useFeatureFlags();
  const { hierarchy, isReady, reload } = useEffectivePermissions();
  const hasAccessRightsManage = useHasPermission(
    PERMISSIONS.ACCESS_RIGHT_MANAGE,
  );

  const isSuperAdmin =
    user?.role?.toLowerCase() === RoleTypeEnum.SUPER_ADMIN?.toLowerCase();

  const hasSettingsPermissions = useHasPermission([
    PERMISSIONS.ORGANIZATION_CONFIG_VIEW,
    PERMISSIONS.ORGANIZATION_CONFIG_MANAGE,
    PERMISSIONS.WEEK_OFF_CONFIG_VIEW,
    PERMISSIONS.WEEK_OFF_CONFIG_MANAGE,
    PERMISSIONS.MASTER_CONFIG_VIEW,
    PERMISSIONS.MASTER_CONFIG_MANAGE,
    PERMISSIONS.FEATURE_FLAGS_VIEW,
    PERMISSIONS.FEATURE_FLAGS_MANAGE,
    PERMISSIONS.ORGANIZATION_ONBOARDING_VIEW,
    PERMISSIONS.ORGANIZATION_ONBOARDING_MANAGE,
    PERMISSIONS.EMAIL_CONFIG_VIEW,
    PERMISSIONS.EMAIL_CONFIG_MANAGE,
    PERMISSIONS.EMAIL_TEMPLATES_VIEW,
    PERMISSIONS.EMAIL_TEMPLATES_MANAGE,
  ]);

  const isAdmin =
    user?.role?.toLowerCase() === RoleTypeEnum.ADMIN?.toLowerCase();

  // Use hierarchy directly from rbac slice (no separate API call)
  const menuStructure = hierarchy || [];

  // Build menu items using the 9-Blueprint Group Architecture
  const menuItems = useMemo((): SidebarMenuItem[] => {
    if (!isReady && !menuStructure.length) {
      return [];
    }

    // 1. Initialize Blueprint Buckets
    const blueprintGroups: Record<
      string,
      {
        label: string;
        icon: unknown;
        code: string;
        items: SidebarMenuItem[];
      }
    > = {
      MAIN: {
        label: "MAIN",
        icon: LayoutDashboard,
        code: "MAIN_GROUP",
        items: [
          {
            icon: LayoutDashboard,
            label: "Dashboard",
            path: "/dashboard",
            code: "DASHBOARD",
          },
        ],
      },
      PEOPLE: {
        label: "PEOPLE",
        icon: Users,
        code: "PEOPLE_GROUP",
        items: [],
      },
      WORKSPACE: {
        label: "WORKSPACE",
        icon: User,
        code: "WORKSPACE_GROUP",
        items: [],
      },
      TIME_LEAVE: {
        label: "TIME & LEAVE",
        icon: Calendar,
        code: "TIME_LEAVE_GROUP",
        items: [],
      },
      LIFECYCLE: {
        label: "LIFECYCLE",
        icon: DoorOpen,
        code: "LIFECYCLE_GROUP",
        items: [],
      },
      PERFORMANCE: {
        label: "PERFORMANCE",
        icon: Star,
        code: "PERFORMANCE_GROUP",
        items: [],
      },
      SURVEY: {
        label: "SURVEY",
        icon: FileStack,
        code: "SURVEY_GROUP",
        items: [],
      },
      RESOURCE_ALLOCATION: {
        label: "RESOURCE ALLOCATION",
        icon: Network,
        code: "RESOURCE_ALLOCATION_GROUP",
        items: [],
      },
      ADMIN_CONFIG: {
        label: "ADMIN CONFIG",
        icon: Settings,
        code: "ADMIN_CONFIG_GROUP",
        items: [],
      },
    };

    // 2. Populate from backend RBAC hierarchy
    for (const module of menuStructure) {
      // Standalone single modules
      if (
        module.code === "ADMIN_EMPLOYEE_MANAGEMENT" ||
        module.code === "EMPLOYEE_MANAGEMENT"
      ) {
        blueprintGroups.PEOPLE.items.push({
          icon: Users,
          label: module.name || "Employees",
          path: "/employees",
          code: module.code,
        });
        continue;
      }

      if (module.code === "ORG_CHART") {
        blueprintGroups.PEOPLE.items.push({
          icon: Network,
          label: module.name || "Org Chart",
          path: "/admin/org-chart",
          code: module.code,
        });
        continue;
      }

      if (
        module.code === "USER_EMPLOYEE_PROFILE" ||
        module.code === "EMPLOYEE_PROFILE"
      ) {
        blueprintGroups.WORKSPACE.items.push({
          icon: User,
          label: module.name || "My Profile",
          path: "/my-profile",
          code: module.code,
        });
        continue;
      }

      if (module.code === "EMPLOYEE_SELF_SERVICE") {
        blueprintGroups.WORKSPACE.items.push({
          icon: UserRoundCog,
          label: module.name || "Employee Self Service",
          path: "/employee-self-service",
          code: module.code,
        });
        continue;
      }

      if (module.code === "HELP_CENTER") {
        blueprintGroups.WORKSPACE.items.push({
          icon: CircleHelp,
          label: module.name || "Help Center",
          path: "/help-center",
          code: module.code,
        });
        continue;
      }

      if (module.code === "ADMIN_REPORTS" || module.code === "REPORTS") {
        blueprintGroups.WORKSPACE.items.push({
          icon: FileText,
          label: module.name || "Reports",
          path: "/reports",
          code: module.code,
        });
        continue;
      }

      if (module.code === "BILLING_LICENSE") {
        blueprintGroups.ADMIN_CONFIG.items.push({
          icon: CreditCard,
          label: module.name || "Billing & License",
          path: "/billing-license",
          code: module.code,
        });
        continue;
      }

      // Skip settings-level standalone modules (they live inside the unified Settings Hub)
      if (
        module.code === "ORGANIZATION_ONBOARDING" ||
        module.code === "ADMIN_ORGANIZATION" ||
        module.code === "CONFIG_MANAGEMENT" ||
        module.code === "EMAIL_MANAGEMENT"
      ) {
        continue;
      }

      // Modules with submodules
      const moduleConfig = getModuleConfig(module.code);
      if (!moduleConfig && !hasAccessRightsManage) {
        continue;
      }

      for (const submodule of module.submodules || []) {
        if (
          submodule.code === "MY_EXIT_VIEW" ||
          submodule.code === "MY_EXIT_MANAGE"
        ) {
          continue;
        }

        // Exclude submodules consolidated inside the Settings Hub (/admin/settings)
        if (
          submodule.code === "ADMIN_CONFIG_MASTER" ||
          submodule.code === "MASTER_CONFIG" ||
          submodule.code === "ADMIN_HOLIDAY_MANAGEMENT" ||
          submodule.code === "ORGANIZATION_CONFIG" ||
          submodule.code === "WORKING_HOURS" ||
          submodule.code === "EMAIL_CONFIG" ||
          submodule.code === "EMAIL_TEMPLATES" ||
          submodule.code === "FEATURE_FLAGS"
        ) {
          continue;
        }

        const path = getPathForPermissionCode(submodule.code);
        const featureFlag = getFeatureFlagForPermissionCode(submodule.code);

        if (!hasAccessRightsManage) {
          if (!path) continue;
          if (
            featureFlag &&
            !isEnabled(FEATURE_FLAGS[featureFlag as keyof typeof FEATURE_FLAGS])
          ) {
            continue;
          }
        } else {
          if (
            featureFlag &&
            !isEnabled(FEATURE_FLAGS[featureFlag as keyof typeof FEATURE_FLAGS])
          ) {
            continue;
          }
        }

        const icon = path ? getIconForSubmodule(submodule.code) : Box;
        const isDisabled = !path;

        let labelName = submodule.name;
        if (
          submodule.code === "APPLY_LEAVE" ||
          submodule.code === "USER_APPLY_LEAVE"
        ) {
          labelName = "My Leaves";
        } else if (
          submodule.code === "ADMIN_LEAVE_APPROVALS" ||
          submodule.code === "LEAVE_APPROVALS" ||
          submodule.code === "TEAM_LEAVE_APPROVALS"
        ) {
          labelName = "Leave Approvals";
        } else if (submodule.code === "MY_ATTENDANCE") {
          labelName = "My Attendance";
        } else if (
          submodule.code === "APPLY_AR" ||
          submodule.code === "USER_ATTENDANCE_REGULARIZATION"
        ) {
          labelName = "Regularization";
        } else if (
          submodule.code === "ADMIN_REGULARIZATION_MANAGEMENT" ||
          submodule.code === "REGULARIZATION_MANAGEMENT" ||
          submodule.code === "TEAM_AR_APPROVALS"
        ) {
          labelName = "Regularization Mgmt";
        } else if (
          submodule.code === "USER_MY_TIMESHEETS" ||
          submodule.code === "MY_TIMESHEETS"
        ) {
          labelName = "My Timesheets";
        } else if (labelName === "Exit approvals") {
          labelName = "Exit Approvals";
        }

        const navItem: SidebarMenuItem = {
          icon,
          label: labelName,
          path: path
            ? path === `/kpi/user-details`
              ? `/kpi/user-details/${user?.id}`
              : path
            : undefined,
          code: submodule.code,
          disabled: isDisabled,
        };

        // Classify submodule into its Blueprint Category
        if (
          module.code === "SEPARATION" ||
          module.code === "TERMINATION" ||
          submodule.code === "RESIGNATION" ||
          submodule.code === "EXIT_APPROVALS" ||
          submodule.code === "EXIT_APPROVALS_VIEW" ||
          submodule.code === "EXIT_APPROVALS_MANAGE" ||
          submodule.code === "TERMINATION_VIEW" ||
          submodule.code === "TERMINATION_INITIATE_MANAGE" ||
          submodule.code === "TERMINATION_APPROVALS_MANAGE"
        ) {
          blueprintGroups.LIFECYCLE.items.push(navItem);
        } else if (
          module.code === "LEAVE_ATTENDANCE" ||
          module.code === "TIMESHEET"
        ) {
          blueprintGroups.TIME_LEAVE.items.push(navItem);
        } else if (module.code === "PERFORMANCE") {
          blueprintGroups.PERFORMANCE.items.push(navItem);
        } else if (module.code === "SURVEYS") {
          blueprintGroups.SURVEY.items.push(navItem);
        } else if (
          module.code === "RESOURCE_ALLOCATION" ||
          module.code === "WORKFLOW"
        ) {
          blueprintGroups.RESOURCE_ALLOCATION.items.push(navItem);
        } else {
          blueprintGroups.ADMIN_CONFIG.items.push(navItem);
        }
      }
    }

    // 3. Transform to Final Menu Items (CRITICAL RULE: Only return groups that have ≥ 1 item)
    const finalItems: SidebarMenuItem[] = [];
    const groupOrder = [
      "MAIN",
      "PEOPLE",
      "WORKSPACE",
      "TIME_LEAVE",
      "LIFECYCLE",
      "PERFORMANCE",
      "SURVEY",
      "RESOURCE_ALLOCATION",
      "ADMIN_CONFIG",
    ];

    for (const key of groupOrder) {
      const group = blueprintGroups[key];
      if (group && group.items.length > 0) {
        finalItems.push({
          icon: group.icon,
          label: group.label,
          type: "group",
          code: group.code,
          items: group.items,
        });
      }
    }

    // 5. Settings as a direct standalone navigation item at the bottom
    // ONLY visible if user is Super Admin, Admin, or has permissions for at least one Settings module
    const canViewSettings =
      isSuperAdmin || isAdmin || hasAccessRightsManage || hasSettingsPermissions;

    if (canViewSettings) {
      finalItems.push({
        icon: Settings,
        label: "Settings",
        path: "/admin/settings",
        type: "single",
        code: "SETTINGS_NAV",
      });
    }

    return finalItems;
  }, [
    menuStructure,
    isEnabled,
    user?.id,
    hasAccessRightsManage,
    hasSettingsPermissions,
    isSuperAdmin,
    isAdmin,
    isReady,
  ]);

  return {
    menuItems,
    loading: !isReady,
    error: null,
    reload,
  };
};

/**
 * Get module configuration (icon, etc.) — delegates to the central registry.
 */
function getModuleConfig(code: string): { icon: unknown } | null {
  return resolveSidebarModuleIcon(code) as { icon: unknown } | null;
}

/**
 * Get icon for submodule based on code — delegates to the central registry.
 */
function getIconForSubmodule(code: string): unknown {
  return resolveSidebarSubmoduleIcon(code);
}

